'use client';

import { ArrowRight, ExternalLink, HeartIcon, Mail, MapPin } from 'lucide-react';
import Link from 'next/link';
import React, { useState } from 'react';

import { FacebookIcon } from '@/components/icons/FacebookIcon';
import { InstagramIcon } from '@/components/icons/InstagramIcon';
import SocialIcon from '@/components/icons/SocialIcon';
import { TwitterIcon } from '@/components/icons/TwitterIcon';
import { YouTubeIcon } from '@/components/icons/YouTubeIcon';
import GradientHeading from '@/components/shared/gradientHeading';
import ShareButton from '@/components/shared/shareButton';

const Section = ({ children }) => <div className="transition-opacity duration-500">{children}</div>;

export default function Footer(props) {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState({
    success: false,
    error: null,
  });

  const handleSubscribe = async e => {
    e.preventDefault();
    if (!email) return;

    // Basic validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSubscribeStatus({
        success: false,
        error: 'Please enter a valid email address',
      });
      return;
    }

    setIsSubmitting(true);
    setSubscribeStatus({ success: false, error: null });

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, source: 'Footer Newsletter Form' }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to subscribe');
      }

      setSubscribeStatus({ success: true, error: null });
      setEmail('');

      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubscribeStatus({ success: false, error: null });
      }, 5000);
    } catch (error) {
      setSubscribeStatus({
        success: false,
        error: error.message || 'An error occurred. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const footerCategories = [
    { name: 'Travel', href: '/category/travel' },
    { name: 'Technology', href: '/category/technology' },
    { name: 'Food', href: '/category/food' },
    { name: 'Health', href: '/category/health-and-fitness' },
  ];

  return (
    <footer className="relative bg-white dark:bg-gray-900">
      {/* Top wave decoration */}
      <div className="absolute top-0 left-0 w-full overflow-hidden">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          className="relative block h-12 w-full rotate-180 fill-current text-gray-100 dark:text-gray-800"
        >
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
        </svg>
      </div>

      <div className="border-t border-gray-200 pt-16 pb-8 dark:border-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Top section with logo and newsletter */}
          <div className="grid grid-cols-1 gap-10 pb-12 md:grid-cols-2 lg:grid-cols-12">
            {/* Logo and tagline */}
            <div className="lg:col-span-4">
              <Section>
                <div className="flex items-center">
                  <GradientHeading level={3}>AllBlogs</GradientHeading>
                </div>
                <p className="mt-3 text-gray-600 dark:text-gray-400">
                  Inspiring ideas, one blog at a time. Discover unique perspectives, practical tips,
                  and captivating stories.
                </p>

                <div className="mt-6 flex items-center space-x-6">
                  {/* YouTube */}
                  <SocialIcon
                    href="https://www.youtube.com/@allblogss"
                    title="AllBlogs YouTube"
                    color="#FF0000"
                  >
                    <YouTubeIcon />
                    <span className="sr-only">YouTube</span>
                  </SocialIcon>

                  {/* Twitter/X */}
                  <SocialIcon href="https://x.com/allblogslive" title="AllBlogs X" color="#1DA1F2">
                    <TwitterIcon />
                    <span className="sr-only">Twitter/X</span>
                  </SocialIcon>

                  {/* Instagram */}
                  <SocialIcon
                    href="https://www.instagram.com/allblogs.in"
                    title="AllBlogs Instagram"
                    color="#E1306C"
                  >
                    <InstagramIcon />
                    <span className="sr-only">Instagram</span>
                  </SocialIcon>

                  {/* Facebook */}
                  <SocialIcon
                    href="https://www.facebook.com/people/Allblogs/100089614301458"
                    title="AllBlogs Facebook"
                    color="#4267B2"
                  >
                    <FacebookIcon />
                    <span className="sr-only">Facebook</span>
                  </SocialIcon>
                  <ShareButton />
                </div>

                <div className="mt-8">
                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <p className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                      Bengaluru, India
                    </p>
                  </div>
                  <div className="mt-2 flex items-center">
                    <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <a
                      href="mailto:support@allblogs.in"
                      className="ml-2 text-sm text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                      support@allblogs.in
                    </a>
                  </div>
                </div>
              </Section>
            </div>

            {/* Newsletter signup */}
            <div className="lg:col-span-4 lg:col-start-9">
              <Section>
                <div className="rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 p-6 shadow-md dark:from-gray-800 dark:to-gray-900">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Subscribe to our newsletter
                  </h3>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Get weekly updates with the latest blogs, tips and insights delivered straight
                    to your inbox.
                  </p>

                  <form className="mt-4" onSubmit={handleSubscribe}>
                    <div className="relative">
                      <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="Your email address"
                        className="w-full rounded-md border border-gray-300 bg-white px-4 py-3 pl-10 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400"
                        required
                        disabled={isSubmitting}
                      />
                      <Mail className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    </div>

                    {subscribeStatus.error && (
                      <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                        {subscribeStatus.error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`mt-3 w-full rounded-md bg-gradient-to-r from-green-600 to-blue-600 px-4 py-3 font-medium text-white shadow-md transition-all hover:shadow-lg dark:from-purple-500 dark:to-indigo-600 ${
                        isSubmitting ? 'opacity-70' : 'hover:-translate-y-1'
                      }`}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <svg
                            className="mr-2 h-4 w-4 animate-spin text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          Subscribe <ArrowRight className="ml-2 h-4 w-4" />
                        </span>
                      )}
                    </button>

                    {subscribeStatus.success && (
                      <div className="mt-3 rounded-md bg-green-100 p-2 text-center text-sm text-green-800 dark:bg-green-900/30 dark:text-green-200">
                        Thank you for subscribing!
                      </div>
                    )}
                  </form>
                </div>
              </Section>
            </div>
          </div>

          {/* Rest of the footer code remains the same */}
          {/* Main footer links */}
          <div className="grid grid-cols-2 gap-8 border-t border-gray-200 py-12 md:grid-cols-4 lg:grid-cols-12 dark:border-gray-800">
            {/* About Us */}
            <div className="lg:col-span-3">
              <Section>
                <h4 className="font-semibold text-gray-900 dark:text-white">About Us</h4>
                <ul className="mt-4 space-y-3">
                  <li>
                    <Link
                      prefetch={false}
                      title="About AllBlogs"
                      href="/about"
                      className="group flex items-center text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                      <span>Our Story</span>
                      <ExternalLink
                        className="ml-1 h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100"
                        aria-hidden="true"
                      />
                    </Link>
                  </li>
                  <li>
                    <Link
                      prefetch={false}
                      title="Our Team"
                      href="/team"
                      className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                      Our Team
                    </Link>
                  </li>
                  <li>
                    <Link
                      prefetch={false}
                      title="Contact Us"
                      href="/contact"
                      className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                      Contact Us
                    </Link>
                  </li>
                  <li>
                    <Link
                      prefetch={false}
                      title="Careers"
                      href="/careers"
                      className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                      Careers
                    </Link>
                  </li>
                </ul>
              </Section>
            </div>

            {/* Resources */}
            <div className="lg:col-span-3">
              <Section>
                <h4 className="font-semibold text-gray-900 dark:text-white">Resources</h4>
                <ul className="mt-4 space-y-3">
                  <li>
                    <Link
                      prefetch={false}
                      title="Blog"
                      href="/"
                      className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                      Blog
                    </Link>
                  </li>
                  <li>
                    <Link
                      prefetch={false}
                      title="Guides"
                      href="/guides"
                      className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                      Guides
                    </Link>
                  </li>
                  <li>
                    <Link
                      prefetch={false}
                      title="FAQ"
                      href="/"
                      className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                      FAQ
                    </Link>
                  </li>
                  <li>
                    <Link
                      prefetch={false}
                      title="Help Center"
                      href="/help"
                      className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                      Help Center
                    </Link>
                  </li>
                </ul>
              </Section>
            </div>

            {/* Categories */}
            <div className="lg:col-span-3">
              <Section>
                <h4 className="font-semibold text-gray-900 dark:text-white">Categories</h4>
                <ul className="mt-4 space-y-3">
                  {footerCategories.map((category, index) => (
                    <li key={index}>
                      <Link
                        prefetch={false}
                        title={category.name}
                        href={category.href}
                        className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                      >
                        {category.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </Section>
            </div>

            {/* Legal */}
            <div className="lg:col-span-3">
              <Section>
                <h4 className="font-semibold text-gray-900 dark:text-white">Legal</h4>
                <ul className="mt-4 space-y-3">
                  <li>
                    <Link
                      prefetch={false}
                      title="Privacy Policy"
                      href="/privacy-policy"
                      className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link
                      prefetch={false}
                      title="Terms & Conditions"
                      href="/terms-conditions"
                      className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                      Terms & Conditions
                    </Link>
                  </li>
                  <li>
                    <Link
                      prefetch={false}
                      title="Cookie Policy"
                      href="/cookie-policy"
                      className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                      Cookie Policy
                    </Link>
                  </li>
                  <li>
                    <Link
                      prefetch={false}
                      title="Accessibility"
                      href="/"
                      className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    >
                      Accessibility
                    </Link>
                  </li>
                </ul>
              </Section>
            </div>
          </div>

          {/* Copyright and badges */}
          <div className="border-t border-gray-200 pt-8 dark:border-gray-800">
            <div className="flex flex-col items-center justify-between space-y-4 md:flex-row md:space-y-0">
              <div className="flex flex-col items-center space-y-2 md:flex-row md:space-y-0 md:space-x-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  © {currentYear} {props?.copyright || 'AllBlogs'}. All rights reserved.
                </p>
                <div className="flex items-center space-x-2">
                  <span className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    Made with <HeartIcon className="mx-1 h-4 w-4 text-red-500" aria-hidden="true" />{' '}
                    in India
                  </span>
                </div>
              </div>

              <div className="flex space-x-4">
                {/* Trust badges */}
                <div className="flex space-x-3">
                  <div className="flex h-10 w-auto items-center rounded-md bg-gray-100 px-2 dark:bg-gray-800">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-auto"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path
                        d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                        fill="rgba(37, 99, 235, 0.1)"
                        stroke="#2563eb"
                      />
                      <polyline points="9 12 11 14 15 10" stroke="#2563eb" />
                    </svg>
                    <span className="ml-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                      Secure
                    </span>
                  </div>

                  <div className="flex h-10 w-auto items-center rounded-md bg-gray-100 px-2 dark:bg-gray-800">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-auto text-green-600"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        fill="rgba(22, 163, 74, 0.1)"
                        stroke="#16a34a"
                      />
                      <path d="M8 11.8571L10.5 14.3572L15.8572 9" stroke="#16a34a" />
                    </svg>
                    <span className="ml-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                      Verified
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
